import React, { useEffect, useRef } from "react";
import requestFrame from "../../../shared/request-frame.js";
import { HEAD_STYLES, drawHeadStyle, headStyleRadius, isHeadStyle, type HeadStyleType } from "../../../game-canvas/head-styles";
import { celebrationCycling } from "./WinnerCelebration";

// The worm head a player plays with, drawn big for the results screen's hero:
// their cosmetic head if they wear one (animated, the same drawing the game
// uses), the plain orange head otherwise. Facing up, like a portrait, except
// the styles that only read one way (FACING): Pacman's mouth opens to the
// right. The canvas is twice
// the disc, centred on it, so a head that draws past its nominal radius (flames,
// a missile's length) is never cut off at the disc's edge.
const HEAD_COLOR = "#FFB74D"; // the renderers' head colour
// The "worm radius" the head is drawn for, from the canvas size, so a bigger
// disc (the winner's) gets a bigger head than the runners-up's.
const bodyRadiusFor = (size: number) => size / 3.2;
// Design aid, with `?fxcycle=1` (see WinnerCelebration): every head style in
// turn, HEAD_CYCLE_SECONDS each, the plain head included.
const HEAD_CYCLE_SECONDS = 2;
const HEAD_CYCLE: (HeadStyleType | null)[] = [null, ...HEAD_STYLES];
// The direction a style is drawn facing on this screen: up (a portrait) unless
// the style only reads one way.
const FACING_UP = -Math.PI / 2;
const FACING: Partial<Record<HeadStyleType, number>> = { pacman: 0 };
const facingFor = (style: HeadStyleType | null) => (style && FACING[style] !== undefined ? FACING[style]! : FACING_UP);

interface Props {
  player: { headStyle?: string | null };
  size: number; // the disc's size in css px
  // the worm radius the head is drawn for; defaults to a share of the disc
  bodyRadius?: number;
  className?: string;
}

export default function WormHeadAvatar({ player, size, bodyRadius, className }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const cw = size * 2; // drawing area: twice the disc, the disc's centre at its centre
    canvas.width = Math.round(cw * dpr);
    canvas.height = Math.round(cw * dpr);
    const cycling = celebrationCycling();
    const BODY_R = bodyRadius ?? bodyRadiusFor(size);
    let style: HeadStyleType | null = isHeadStyle(player.headStyle) ? player.headStyle : null;
    let cycleIndex = 0;
    const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let id = 0;
    let running = true;
    let t0 = performance.now();
    const frame = () => {
      if (!running) return;
      if (cycling && performance.now() - t0 > HEAD_CYCLE_SECONDS * 1000) {
        cycleIndex = (cycleIndex + 1) % HEAD_CYCLE.length;
        style = HEAD_CYCLE[cycleIndex];
        t0 = performance.now();
      }
      const r = headStyleRadius(BODY_R, style);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cw, cw);
      const drawn = style ? drawHeadStyle(ctx, cw / 2, cw / 2, r, facingFor(style), (performance.now() - t0) / 1000, style) : false;
      if (!drawn) {
        ctx.fillStyle = HEAD_COLOR;
        ctx.beginPath();
        ctx.arc(cw / 2, cw / 2, headStyleRadius(BODY_R), 0, Math.PI * 2);
        ctx.fill();
      }
      // a plain head is static; a cosmetic one may animate
      if ((style || cycling) && !reduced) id = requestFrame(frame);
    };
    id = requestFrame(frame);
    return () => {
      running = false;
      window.cancelAnimationFrame(id);
    };
  }, [player.headStyle, size, bodyRadius]);
  // Above the celebration layer (see .hero-fx): the celebration crosses the
  // ring but never covers the head itself.
  return <canvas ref={ref} className={className} style={{ width: size * 2, height: size * 2, margin: -size / 2, flex: "0 0 auto", position: "relative", zIndex: 3 }} aria-hidden="true" />;
}
